import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Mic, Square } from "lucide-react";
import PQueue from "p-queue";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getListDocumentsQueryKey,
  useCreateUpload,
} from "~/api/default/default";
import {
  useCompleteMultipartUpload,
  useGetMultipartPartUrl,
  useStartMultipartUpload,
} from "~/api/documents/documents";
import { useGetCurrentUser } from "~/api/users/users";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Modal } from "~/components/ui/modal";
import { uploadChunkToS3 } from "~/lib/s3";
import { trackEvent } from "~/utils/analytics";
import { convertWebmToMp3 } from "~/utils/audio-converter";
import logger from "~/utils/logger";
import { useBatchKey } from "../hooks/useBatchKey";

const MAX_TITLE_LENGTH = 100;
const OPTIMAL_RECORDING_LENGTH_SECONDS = 60 * 60; // 1 hour in seconds

const sanitizeFilename = (title: string): string => {
  let sanitized = title
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, "")
    .replace(/\.+/g, ".")
    .replace(/^\.+|\.+$/g, "")
    .trim();

  if (sanitized.length > MAX_TITLE_LENGTH) {
    sanitized = sanitized.substring(0, MAX_TITLE_LENGTH);
  }

  if (!sanitized || sanitized === "." || sanitized === "..") {
    sanitized = "Voice Note";
  }

  return sanitized;
};

const formatDefaultTitle = (
  date: Date,
  intl: ReturnType<typeof useIntl>
): string => {
  const day = date.getDate();
  const month = date.toLocaleString(intl.locale, { month: "short" });
  const year = date.getFullYear();
  return `Voice Note - ${day} ${month} ${year}`;
};

const MAX_CONCURRENT_CHUNKS = 6;
const CHUNK_SIZE = 6 * 1024 * 1024; // 6MB - AWS S3 requires minimum 5MB for multipart uploads (except last part)

type RecordingState =
  | { status: "idle" }
  | { status: "recording"; startTime: number }
  | { status: "naming"; audioBlob: Blob }
  | { status: "uploading" }
  | { status: "error"; message: string };

interface VoiceRecorderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  caseId: string;
}

export const VoiceRecorderModal: FC<VoiceRecorderModalProps> = ({
  open,
  onOpenChange,
  organizationId,
  caseId,
}) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [recordingState, setRecordingState] = useState<RecordingState>({
    status: "idle",
  });
  const [elapsedTime, setElapsedTime] = useState(0);
  const [voiceNoteTitle, setVoiceNoteTitle] = useState("");

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const { mutateAsync: createUpload } = useCreateUpload();
  const { mutateAsync: startMultipartUpload } = useStartMultipartUpload();
  const { mutateAsync: getMultipartPartUrl } = useGetMultipartPartUrl();
  const { mutateAsync: completeMultipartUpload } = useCompleteMultipartUpload();
  const { data: currentUser } = useGetCurrentUser();

  const { sessionBatchKey } = useBatchKey({
    userId: currentUser?.id || "",
  });

  const [uploadQueue] = useState(
    () => new PQueue({ concurrency: MAX_CONCURRENT_CHUNKS })
  );

  const cleanupRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    audioChunksRef.current = [];
  }, []);

  useEffect(() => {
    if (!open) {
      cleanupRecording();
      setRecordingState({ status: "idle" });
      setElapsedTime(0);
      setVoiceNoteTitle("");
    }
  }, [open, cleanupRecording]);

  useEffect(() => {
    if (open) {
      trackEvent({
        eventName: "app/case/voice_recorder.modal/open",
        properties: { caseId, organizationId },
      });
    }
  }, [open, caseId, organizationId]);

  const startRecording = async () => {
    try {
      trackEvent({
        eventName: "app/case/voice_recorder.record_button/click",
        properties: { caseId, organizationId },
      });

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(1000);

      const startTime = Date.now();
      setRecordingState({ status: "recording", startTime });

      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    } catch (error) {
      logger.error("Failed to start recording:", error);
      setRecordingState({
        status: "error",
        message: intl.formatMessage({
          id: "voiceRecorder.error.microphoneAccess",
          defaultMessage:
            "Could not access microphone. Please check your permissions.",
        }),
      });
    }
  };

  const stopRecording = async () => {
    if (!mediaRecorderRef.current) return;

    trackEvent({
      eventName: "app/case/voice_recorder.stop_button/click",
      properties: {
        caseId,
        organizationId,
        recordingDurationSeconds: elapsedTime,
      },
    });

    return new Promise<void>((resolve) => {
      if (!mediaRecorderRef.current) {
        resolve();
        return;
      }

      mediaRecorderRef.current.onstop = () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });

        const defaultTitle = formatDefaultTitle(new Date(), intl);
        setVoiceNoteTitle(defaultTitle);
        setRecordingState({ status: "naming", audioBlob });
        resolve();
      };

      mediaRecorderRef.current.stop();

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    });
  };

  const handleSaveTitle = async () => {
    if (recordingState.status !== "naming") return;

    const sanitizedTitle = sanitizeFilename(voiceNoteTitle);

    trackEvent({
      eventName: "app/case/voice_recorder.save_title/click",
      properties: {
        caseId,
        organizationId,
        titleLength: sanitizedTitle.length,
        isDefaultTitle: sanitizedTitle.startsWith("Voice Note -"),
      },
    });

    setRecordingState({ status: "uploading" });

    try {
      await uploadRecording(recordingState.audioBlob, sanitizedTitle);
    } catch (error) {
      logger.error("Failed to upload recording:", error);
      setRecordingState({
        status: "error",
        message: intl.formatMessage({
          id: "voiceRecorder.error.uploadFailed",
          defaultMessage: "Failed to upload recording. Please try again.",
        }),
      });
    }
  };

  const uploadRecording = async (audioBlob: Blob, title: string) => {
    const uploadStartTime = Date.now();

    try {
      logger.info("[VoiceRecorder] Converting WebM to MP3...", {
        originalSize: audioBlob.size,
      });

      const mp3Blob = await convertWebmToMp3(audioBlob, 96);

      logger.info("[VoiceRecorder] Conversion completed", {
        originalSize: audioBlob.size,
        mp3Size: mp3Blob.size,
        compressionRatio: ((1 - mp3Blob.size / audioBlob.size) * 100).toFixed(
          1
        ),
      });

      const mp3File = new File([mp3Blob], `${title}.mp3`, {
        type: "audio/mpeg",
      });

      const payload = {
        filename: mp3File.name,
        contentType: mp3File.type,
        size: mp3File.size,
        batchKey: sessionBatchKey,
      };

      logger.info("[VoiceRecorder] Creating upload", payload);

      const upload = await createUpload({
        organizationId,
        projectId: caseId,
        data: payload,
      });

      logger.info("[VoiceRecorder] Upload created", { uploadId: upload.id });

      const startResp = await startMultipartUpload({
        organizationId,
        projectId: caseId,
        uploadId: upload.id,
        data: {
          contentType: mp3File.type,
          filename: mp3File.name,
          fileSize: mp3File.size,
        },
      });

      const { uploadId, key } = startResp;

      const chunkCount = Math.ceil(mp3File.size / CHUNK_SIZE);
      const uploadedParts: { ETag: string; PartNumber: number }[] = [];

      const uploadChunk = async (index: number) => {
        const partNumber = index + 1;
        const start = index * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, mp3File.size);
        const chunk = mp3File.slice(start, end);

        const partUrlData = await getMultipartPartUrl({
          organizationId,
          projectId: caseId,
          uploadId: upload.id,
          data: {
            key,
            uploadId,
            partNumber,
          },
        });

        const etagHeader = await uploadChunkToS3(
          partUrlData.signedUrl as string,
          chunk,
          mp3File.type,
          undefined,
          undefined
        );

        if (!etagHeader) {
          throw new Error("Missing ETag in S3 response");
        }

        uploadedParts.push({
          ETag: etagHeader.replace(/"/g, ""),
          PartNumber: partNumber,
        });
      };

      const chunkPromises: Promise<void>[] = [];
      for (let i = 0; i < chunkCount; i++) {
        chunkPromises.push(
          uploadQueue.add(() => uploadChunk(i), {
            priority: 0,
          })
        );
      }
      await Promise.all(chunkPromises);

      await completeMultipartUpload({
        organizationId,
        projectId: caseId,
        uploadId: upload.id,
        data: {
          fileHash: `voice-note-${Date.now()}`,
          key,
          uploadId,
          parts: uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber),
        },
      });

      logger.info("[VoiceRecorder] Multipart upload completed", {
        uploadId: upload.id,
        key,
        partsCount: uploadedParts.length,
      });

      trackEvent({
        eventName: "app/case/voice_recorder.upload/success",
        properties: {
          caseId,
          organizationId,
          voiceNoteId: upload.id,
          recordingDurationSeconds: elapsedTime,
          originalFileSizeBytes: audioBlob.size,
          mp3FileSizeBytes: mp3Blob.size,
          uploadDurationMs: Date.now() - uploadStartTime,
          chunksCount: chunkCount,
        },
      });

      queryClient.invalidateQueries({
        queryKey: getListDocumentsQueryKey(organizationId, caseId),
      });

      setTimeout(() => {
        onOpenChange(false);
      }, 1000);
    } catch (error) {
      logger.error("Failed to upload recording:", error);
      throw error;
    }
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const getModalTitle = () => {
    if (recordingState.status === "uploading") {
      return intl.formatMessage({
        id: "voiceRecorder.title.uploading",
        defaultMessage: "Uploading...",
      });
    }
    if (recordingState.status === "naming") {
      return intl.formatMessage({
        id: "voiceRecorder.title.naming",
        defaultMessage: "Name Your Voice Note",
      });
    }
    return intl.formatMessage({
      id: "voiceRecorder.title.default",
      defaultMessage: "Record Voice Note",
    });
  };

  const getModalDescription = () => {
    if (recordingState.status === "recording") {
      return intl.formatMessage({
        id: "voiceRecorder.description.recording",
        defaultMessage: "Recording in progress. Click Stop when finished.",
      });
    }
    if (recordingState.status === "uploading") {
      return intl.formatMessage({
        id: "voiceRecorder.description.uploading",
        defaultMessage: "Please wait while your recording is being uploaded.",
      });
    }
    if (recordingState.status === "naming") {
      return intl.formatMessage({
        id: "voiceRecorder.description.naming",
        defaultMessage: "Enter a title for your voice note before uploading.",
      });
    }
    return intl.formatMessage({
      id: "voiceRecorder.description.default",
      defaultMessage:
        "Click the microphone button to start recording your clinical note.",
    });
  };

  const isRecording = recordingState.status === "recording";
  const isUploading = recordingState.status === "uploading";
  const isNaming = recordingState.status === "naming";
  const hasError = recordingState.status === "error";
  const isOverOptimalLength = elapsedTime >= OPTIMAL_RECORDING_LENGTH_SECONDS;

  const renderContent = () => {
    if (isUploading) {
      return (
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 dark:text-blue-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <FormattedMessage
              id="voiceRecorder.status.uploading"
              defaultMessage="Uploading your recording..."
            />
          </p>
        </div>
      );
    }

    if (isNaming) {
      return (
        <div className="w-full space-y-4">
          <div className="space-y-2">
            <Label htmlFor="voice-note-title">
              <FormattedMessage
                id="voiceRecorder.label.title"
                defaultMessage="Title"
              />
            </Label>
            <Input
              id="voice-note-title"
              value={voiceNoteTitle}
              onChange={(e) => setVoiceNoteTitle(e.target.value)}
              maxLength={MAX_TITLE_LENGTH}
              placeholder={intl.formatMessage({
                id: "voiceRecorder.placeholder.title",
                defaultMessage: "Enter a title for your voice note",
              })}
              autoFocus
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              <FormattedMessage
                id="voiceRecorder.hint.titleCharacters"
                defaultMessage="{count} / {max} characters"
                values={{ count: voiceNoteTitle.length, max: MAX_TITLE_LENGTH }}
              />
            </p>
          </div>
        </div>
      );
    }

    return (
      <>
        <button
          onClick={isRecording ? stopRecording : startRecording}
          disabled={isUploading}
          className={`rounded-full p-8 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            isRecording
              ? "bg-red-500 hover:bg-red-600 focus:ring-red-500 animate-pulse"
              : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
          }`}
          aria-label={
            isRecording
              ? intl.formatMessage({
                  id: "voiceRecorder.button.stop.ariaLabel",
                  defaultMessage: "Stop recording",
                })
              : intl.formatMessage({
                  id: "voiceRecorder.button.record.ariaLabel",
                  defaultMessage: "Start recording",
                })
          }
        >
          {isRecording ? (
            <Square className="w-8 h-8 text-white" fill="white" />
          ) : (
            <Mic className="w-8 h-8 text-white" />
          )}
        </button>

        {isRecording && (
          <div className="text-center">
            <p
              className={`text-2xl font-mono font-semibold ${
                isOverOptimalLength
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-gray-900 dark:text-gray-100"
              }`}
            >
              {formatTime(elapsedTime)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              <FormattedMessage
                id="voiceRecorder.status.recording"
                defaultMessage="Recording..."
              />
            </p>
            {isOverOptimalLength && (
              <div className="mt-3 flex items-center justify-center gap-2 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <p className="text-sm">
                  <FormattedMessage
                    id="voiceRecorder.warning.optimalLength"
                    defaultMessage="Recording exceeds recommended 1-hour length"
                  />
                </p>
              </div>
            )}
          </div>
        )}

        {!isRecording && !hasError && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            <FormattedMessage
              id="voiceRecorder.hint.clickToRecord"
              defaultMessage="Click to start recording"
            />
          </p>
        )}

        {hasError && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3 max-w-sm">
            <p className="text-red-800 dark:text-red-200 text-sm text-center">
              {recordingState.message}
            </p>
          </div>
        )}
      </>
    );
  };

  const renderFooter = () => {
    if (isUploading) {
      return null;
    }

    if (isNaming) {
      return (
        <div className="flex gap-2 w-full justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button onClick={handleSaveTitle} disabled={!voiceNoteTitle.trim()}>
            <FormattedMessage
              id="voiceRecorder.button.save"
              defaultMessage="Save"
            />
          </Button>
        </div>
      );
    }

    return (
      <Button
        variant="outline"
        onClick={() => onOpenChange(false)}
        disabled={isUploading}
      >
        <FormattedMessage id="cancel" defaultMessage="Cancel" />
      </Button>
    );
  };

  return (
    <Modal
      open={open}
      onOpenChange={(newOpen) => {
        if (!isUploading && !isNaming) {
          onOpenChange(newOpen);
        }
      }}
      title={getModalTitle()}
      description={getModalDescription()}
      content={
        <div className="flex flex-col items-center justify-center py-8 gap-6">
          {renderContent()}
        </div>
      }
      footer={renderFooter()}
    />
  );
};
